import { Button, Col, Input, Popover, Row, Space, Switch, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Key, useCallback, useMemo, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import CopyableContent from "../../../../components/copyableContent";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import { NewCustomEquipment } from "../../../../components/modal/addCustomEquipment";
import openModal from "../../../../components/modal/openModal";
import PopMaybeOver from "../../../../components/popMaybeOver";
import _t from "../../../../lang/translate";
import { EnumItemType } from "../../../../types/appTypes";
import { EquipmentCatalog } from "../../../../types/newPriceTypes";
import format from "../../../../utilities/formatNumbers";
import { constructMotorAtlasLink } from "../../../../utilities/stringExtraFunctions";
import debounce from "lodash.debounce";
import EstimaticOut from '../../../../assets/images/estimatic-out.png'

const categories = new Map<number, EnumItemType>([
  [2, { "id": 2, "title": "Normalt udstyr" }],
  [3, { "id": 3, "title": "Radio/HiFi udstyr" }],
  [4, { "id": 4, "title": "Alufælge" }],
  [5, { "id": 5, "title": "Trækkrog" }],
  [6, { "id": 6, "title": "Mobiltelefonudstyr" }],
  [7, { "id": 7, "title": "Sikkerhedsudstyr" }],
  [8, { "id": 8, "title": "Lak" }]
]);

type EquipmentManagerProps = {
  equipment: EquipmentCatalog[];
  setMounted: (code: string, checked: boolean) => void;
  addCustomEquipment: (eq: NewCustomEquipment) => void
  reloadData: () => Promise<void>;
  vehicle: {
    vin: string | null;
    motorAtlasId: string | null;
    firstRegDate: string | null;
    type: number | null | undefined
  }
}

const looksLikeCodeList = (s: string) => s.split(",").length > 1;
export default function EquipmentManager({ equipment, setMounted, addCustomEquipment, reloadData, vehicle }: EquipmentManagerProps) {
  const hasVin = !!vehicle.vin;
  const [showAvailableEquipment, setShowAvailableEquipment] = useState(() => {
    if (!equipment.length) return false
    return !hasVin || !equipment?.some(eq => eq.mounted || eq.isForceMounted)
  })
  const [showPackagesAndNPEquipment, setShowPackagesAndNPEquipment] = useState(false)

  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly Key[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [reloading, setReloading] = useState(false)

  const debouncedSearch = useCallback(
    debounce((val: string) => {
      setSearchValue(val)
    }, 250)
    , [setSearchValue]
  )

  const filteredDataSource = useMemo(() => {
    const nSearch = searchValue.toLowerCase()
    const equipmentCodes = new Set(nSearch.split(",").map(term => term.trim()))
    const seemsToBeCodeList = looksLikeCodeList(nSearch);

    let searched = equipment;
    // TODO: How to search for equipment inside the eq.packageContent list??
    // When searching for equipment, it also has to search inside the package itself.
    if (nSearch) {
      searched = equipment.filter(item => {
        if (seemsToBeCodeList && !!item.code) {
          return equipmentCodes.has(item.code.toLowerCase().trim())
        }
        const codeMatch = item.code?.toLowerCase().includes(nSearch)
        const nameMatch = item.name?.toLowerCase().includes(nSearch)
        const sourceMatch = item.source?.toLowerCase().includes(nSearch)
        return codeMatch || nameMatch || sourceMatch
      })
    } else {
      searched = equipment
        .filter(item => item.isVinEquipment || item.mounted || item.isForceMounted || showAvailableEquipment)
    }

    return searched.toSorted(sortEquipment)
  }, [equipment, showAvailableEquipment, searchValue, showPackagesAndNPEquipment])

  const dataSource = useMemo(() => {
    const dataMap = new Map(filteredDataSource.map((eq) => [eq.code, eq]))
    const equipmentCodeMap = new Map(equipment.map((eq) => [eq.code, eq]))
    for (const eq of filteredDataSource) {
      if (!eq.isIncludedInMountedPackage) {
        continue;
      }

      for (const code of eq.includedInPackage) {
        const packageEq = equipmentCodeMap.get(code)
        if (packageEq && (packageEq.mounted || packageEq.isForceMounted)) dataMap.set(code, packageEq)
      }
    }
    if (showPackagesAndNPEquipment) {
      return [...dataMap.values()]
    }
    return [...dataMap.values()].filter(eq => !eq.isIncludedInMountedPackage && !eq.includedInNewPrice)
  }, [filteredDataSource, equipment, showAvailableEquipment, searchValue, showPackagesAndNPEquipment])

  async function handleReload() {
    setReloading(true)
    await reloadData()
    setReloading(false)
  }

  const columns: ColumnsType<EquipmentCatalog> = [
    {
      title: _t("chosen"),
      key: "mounted",
      dataIndex: "mounted",
      width: 64,
      sorter: (a, b) => Number(a.mounted || a.isForceMounted) - Number(b.mounted || b.isForceMounted),
      render(value, record, index) {
        let title = undefined;
        const content = []
        if (record.isForceMounted) {
          title = "Tvangsmonteret";
          if (record.isForceMountedByNewPrice && record.includedInNewPrice) {
            content.push("Inkluderet i den valgte nypris")
          }
          if (record.isForceMountedByNewPrice && !record.includedInNewPrice) {
            content.push("Monteret med pris i MotorAtlas")
          }
          if (record.isForceMountedByPackage) {
            content.push("Inkluderet via en monteret pakke")
          }
        }
        return (
          <PopMaybeOver title={title} overlayInnerStyle={{ whiteSpace: "pre-line" }} content={content.join('\r\n')}>
            <Switch checked={record.mounted || record.isForceMounted} size="small"
              disabled={record.isForceMounted}
              onChange={checked => {
                if (record.isForceMounted) return;
                setMounted(record.code ?? "", checked)
              }}
            />
          </PopMaybeOver>
        )
      },
    },
    {
      title: _t("code"),
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      width: 64,
      sorter: (a, b) => a.code?.localeCompare(b.code ?? "") ?? 0,
    },
    {
      title: _t("name"),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name?.localeCompare(b.name ?? "") ?? 0,
      sortDirections: ["ascend", "descend"],
      render: (value: string, record) => {
        return <>
          {record.packageContent.length > 0 && (record.mounted || record.isForceMounted) && !record.isIncludedInMountedPackage ? (
            <span style={{ textDecoration: "underline" }}
              onClick={() => {
                setExpandedRowKeys((prev) => {
                  return prev.includes(record.code ?? "")
                    ? prev.filter(key => key !== record.code)
                    : [...prev, record.code ?? ""]
                })
              }}
            >
              {value}
            </span>
          ) : value}
          &nbsp;
          {record.packageContent.length ? (
            <CopyableContent displayContent contentToCopy={record.packageContent.join(', ')}>
              <Icon color="#0299fb" size="large" name="copy-outline" />
            </CopyableContent>
          ) : null}
        </>
      },
    },
    {
      title: _t("type"),
      sorter: (a, b) => {
        const primary = Number(b.includedInNewPrice) - Number(a.includedInNewPrice);
        if (primary !== 0) return primary;

        return Number(b.isIncludedInMountedPackage) - Number(a.isIncludedInMountedPackage);
      },
      render: (_, record) => {
        return (
          <Space>
            <Popover
              overlayClassName="popover-no-content-padding"
              title={record.isIncludedInMountedPackage ?
                `Pakke: Udstyr er inkluderet via en monteret pakke (${record.includedInPackage.join(", ")})`
                : "Pakke: Ikke inkluderet"}
              content={null}
            >
              <Icon color={record.isIncludedInMountedPackage ? "#0299fb" : "#EFF3F5"} size="large" name="package" />
            </Popover>
            <Popover
              overlayClassName="popover-no-content-padding"
              title={record.includedInNewPrice ? "Nypris: Udstyr inkluderet i nypris (direkte eller via pakke)" : "Nypris: Ikke inkluderet"}
              content={null}
            >
              <Icon color={record.includedInNewPrice ? "#0299fb" : "#EFF3F5"} size="large"
                name="currency-circle-dollar" />
            </Popover>
          </Space>
        )
      },
      width: 64,
    },
    {
      key: "source",
      dataIndex: "source",
      title: _t("source"),
      sorter: (a, b, dir) => {
        const weightA = equipmentSourceOrderMap?.[a?.source as string] ?? 9999
        const weightB = equipmentSourceOrderMap?.[b?.source as string] ?? 9999
        return weightA - weightB
      },
      width: 112,
    },
    {
      key: "category",
      dataIndex: "category",
      title: _t("category"),
      width: 120,
      sorter: (a, b, dir) => (a.category ?? 2) - (b.category ?? 2),
      render: (value) => categories.get(value)?.title,
    },
    {
      key: "amount",
      dataIndex: "amount",
      title: _t("new_price"),
      width: 120,
      sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (value, record) => {
        const price = format.price(value)
        if (record.isIncludedInMountedPackage || record.includedInNewPrice) {
          return (
            <span>
              <span style={{ textDecoration: "line-through", color: "#95A5AE", marginRight: 4 }}>
                {price}
              </span>
              <span>0 kr.</span>
            </span>
          )
        }
        return price
      },
    },

  ]

  async function handleAddCustomEquipment() {
    try {
      const customEquipment = await openModal("addCustomEquipment", null) as NewCustomEquipment
      addCustomEquipment(customEquipment)
    } catch (err) {
      renderErrorMessage(err)
    }
  }
  function redirectToMotorAtlas() {
    if (!vehicle.motorAtlasId) {
      renderErrorMessage("Ingen E-kode valgt. Åbn først modelkatalogen og vælg en specifik model for at vælge en E-kode.")
      return;
    }
    const href = constructMotorAtlasLink(vehicle.motorAtlasId ?? "", "udstyr", vehicle.firstRegDate, vehicle.type === 2)
    window.open(href, "_blank")
  }
  function copyMountedCodes() {
    const codes = equipment?.filter(eq => !!eq.mounted || !!eq.isForceMounted)?.map(eq => eq.code)?.join(", ")
    window.navigator.clipboard.writeText(`${codes || ''}`);
  }

  function copyMountedEquipmentList() {
    try {
      const estimaticEquipment = [...equipment]
        .filter(item => item.mounted || item.isForceMounted)
        .map(item => {

          let price = item.amount ?? 0
          if (item.includedInNewPrice || item.isIncludedInMountedPackage) {
            price = 0;
          }
          if (price) price = Math.round(price)
          return {
            code: item.code ?? null,
            name: item.name ?? null,
            category: item.category,
            price: price
          }
        })
      window.navigator.clipboard.writeText(JSON.stringify(estimaticEquipment));
    } catch (err) {
      renderErrorMessage(err)
    }
  }

  return (
    <div>
      <Row style={{
        borderTop: "1px solid #EFF3F5",
        borderLeft: "1px solid #EFF3F5",
        borderRight: "1px solid #EFF3F5",
        borderBottom: "none",
        paddingTop: 2,
        paddingBottom: 2
      }} justify="space-between">
        <Col flex={1}>
          <Input bordered={false}
            allowClear
            prefix={<Icon name="search" />}
            style={{ height: "100%", width: "100%" }}
            onChange={(e) => debouncedSearch(e.target.value)}
            placeholder={_t("search") + "..."}
          />
        </Col>
        <Col style={{ padding: 8 }}>
          <Space size="small">
            <Tooltip
              title={!equipment.length ? _t("none", "availableEquipment")
                : showAvailableEquipment ? _t("hide", "availableEquipment") : _t("show", "availableEquipment")}>
              <Button type="text" shape="circle"
                style={{ color: showAvailableEquipment ? "#0299fb" : undefined }}
                icon={<Icon name={showAvailableEquipment ? "eye-outline" : "eye-off-outline"} />}
                disabled={!equipment.length}
                onClick={() => setShowAvailableEquipment(!showAvailableEquipment)}
              />
            </Tooltip>
            <Tooltip
              title={showPackagesAndNPEquipment ? "Vis udstyr indkluderet i pakker eller nypris" : "Skjul udstyr indkluderet i pakker eller nypris"}>
              <Button type="text" shape="circle"
                style={{ color: showPackagesAndNPEquipment ? "#0299fb" : undefined }}
                icon={<Icon name={"package"} />}
                onClick={() => setShowPackagesAndNPEquipment(!showPackagesAndNPEquipment)}
              />
            </Tooltip>
            <Button type="text" icon={<Icon name="copy-outline" />} onClick={copyMountedCodes} title={_t('copy', 'mounted')} />
            <Button type="text"
              icon={<img height={24} width={24} src={EstimaticOut} />}
              onClick={copyMountedEquipmentList}
              title={_t('copy_to_clipboard')}
            />

            <Button onClick={redirectToMotorAtlas} >
              {_t("configureECode")}
              <Icon name="arrow-square-in" />
            </Button>
            <Button
              disabled={reloading}
              onClick={handleReload}
              icon={<Icon className={reloading ? "animate-spin" : ""} name="sync-outline" />}
            />
            <Button onClick={handleAddCustomEquipment} type="primary"
              title="Hint: Overskriv custom udstyr ved at tilføje den igen med samme kode"
            >{_t("add")}</Button>

          </Space>
        </Col>
      </Row>
      <Table columns={columns} dataSource={dataSource}
        pagination={{
          showTotal: (total, range) => `${range[0]}-${range[1]} af ${total} (${equipment.length})`,
          pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
          defaultPageSize: 100,
          showSizeChanger: true,
        }}
        onRow={(record) => {
          let className = ""
          if (record.isVinEquipment) className += " bold"
          if (record.mounted || record.isForceMounted) className += " mounted"
          if (record.packageContent.length > 0) {
            className += " has-subequipment"
          }

          return {
            className
          }
        }}
        rowKey={'code'}
        size="small"
        scroll={{ x: 800, y: "calc(100vh - 552px)" }}
        tableLayout="fixed"
        className={`car-api-equipment-selector-table`}
        expandable={{
          expandRowByClick: false,
          showExpandColumn: false,
          expandedRowKeys: expandedRowKeys,
          expandedRowRender: (record) => {
            const packageEquipment = mountFrom(record, equipment)
            return (
              <div>
                <Table columns={columns} dataSource={packageEquipment}
                  pagination={false}
                  showHeader={false}
                  onRow={(record) => {
                    let className = ""
                    if (record.isVinEquipment) className += " bold"
                    return {
                      className
                    }
                  }}
                  rowKey={'code'}
                  size="small"
                  scroll={{ x: 800 }}
                  tableLayout="fixed"
                />

              </div>

            )
          },
          rowExpandable(record) {
            return !!record.packageContent.length && (record.mounted || record.isForceMounted)
          },
        }}

      />
    </div>
  )
}

const equipmentSourceOrderMap: Record<string, number> = {
  "DMR-Afgørelse": 1,
  "Importør": 2,
  "Estimatic": 3,
  "DAT": 4,
  "Brugerdefineret": 5
};

function mountFrom(root: EquipmentCatalog, equipment: EquipmentCatalog[]) {
  const seenCodes = new Set<string>()
  const equipmentMap = new Map(equipment.map((e) => [e.code, e]))
  const flatChildMap = new Map<string, EquipmentCatalog>()
  function inner(eq: EquipmentCatalog) {
    if (!eq.code) return;
    if (seenCodes.has(eq.code)) return;
    seenCodes.add(eq.code);

    if (eq.packageContent.length) {
      for (const childCode of eq.packageContent) {
        const child = equipmentMap.get(childCode)
        if (child) flatChildMap.set(childCode, child)
        if (child?.packageContent.length) inner(child)
      }
    }
  }
  inner(root)
  return Array.from(flatChildMap.values())
}

function sortEquipment(a: EquipmentCatalog, b: EquipmentCatalog): number {
  // NOTE: Sort the dataSource/equipment list
  // 1. By mounted
  // 2. By packages -> Such that mounted packages are first
  // 3. By alphabetic code

  const aMounted = a.mounted || a.isForceMounted
  const bMounted = b.mounted || b.isForceMounted
  if (aMounted && !bMounted) return -1;
  if (!aMounted && bMounted) return 1;

  if (a.packageContent.length > b.packageContent.length) return -1;
  if (a.packageContent.length < b.packageContent.length) return 1;



  const codeAscendingCompare = a.code?.toLowerCase().localeCompare(b?.code?.toLowerCase() ?? "")
  if (codeAscendingCompare && codeAscendingCompare !== 0) return codeAscendingCompare;

  return 0
}
